import type { SymbolID } from '../types';

/**
 * Verify if value is empty or not
 *
 * @param {any} obj Value to check
 * @return {boolean}
 */
function isEmpty(obj: any): boolean {
  return [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;
}

/**
 * Verify if is a function
 *
 * @param  {any} func Value to check
 * @return {boolean}
 */
function isFunction(func: any): boolean {
  return typeof func === 'function';
}

/**
 * Creates a symbol with a mandatory description/label
 *
 * @param {string} key Label for symbol
 * @return {SymbolID}
 */
function createSymbolID(key: string, globally = false): SymbolID {
  return { key: globally ? Symbol.for(key) : Symbol(key) };
}

/**
 * Verify window is available
 *
 * @return {boolean}
 */
function isWindow(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Verify url contains localhost on it and defined
 * current env as local.
 *
 * @return {boolean}
 */
function isLocalhost(): boolean {
  return isWindow() && location.hostname.includes('localhost');
}

/**
 * Verify if value is Symbol type
 *
 * @param {any} value Value to check
 * @return {boolean}
 */
function isSymbol(value: any): boolean {
  return (
    typeof value === 'symbol' ||
    (typeof value === 'object' && Object.prototype.toString.call(value) === '[object Symbol]')
  );
}

/**
 * Verify if value is String type
 *
 * @param {any} value Value to check
 * @return {boolean}
 */
function isString(value: any): boolean {
  return Object.prototype.toString.call(value) === '[object String]';
}

/**
 * Verify if value is SymbolID custom type.
 *
 * @param {any} value Value to check
 * @return {boolean}
 */
function isSymbolID(value: any): boolean {
  return value?.key && isSymbol(value.key);
}

/**
 * Verify if value is Object type
 *
 * @param  {unknown} value Value to check
 * @return {boolean}
 */
function isObject(value: unknown): boolean {
  return Object.prototype.toString.call(value) === '[object Object]';
}

/**
 * Verify if is a plain object
 *
 * @param  {unknown} obj Value to check
 * @return {obj}
 */
function isPlainObject(obj: unknown): obj is Record<string | symbol, any> {
  // Basic check for Type object that's not null
  if (typeof obj === 'object' && obj !== null) {
    // If Object.getPrototypeOf supported, use it
    if (typeof Object.getPrototypeOf === 'function') {
      const proto = Object.getPrototypeOf(obj);
      return proto === Object.prototype || proto === null;
    }

    // Otherwise, use internal class
    // This should be reliable as if getPrototypeOf not supported, is pre-ES5
    return isObject(obj);
  }

  // Not an object
  return false;
}

export {
  createSymbolID,
  isWindow,
  isLocalhost,
  isSymbol,
  isString,
  isSymbolID,
  isEmpty,
  isFunction,
  isPlainObject,
  isObject,
};
